import { useDocsDataModelStore, useSearchableTreeList } from '@/models/docs-data/docsData';
import clsx from 'clsx';
import React from 'react';
import { useLevelNavbarGroup } from '../docs-layout/components/navbar-content/hooks/useLevelNavbarGroup';
import { RootBackFolders } from './components/root-back-folders/RootBackFolders';
import SidebarSearch from './components/sidebar-search';
import { SidebarTreeList } from './components/sidebar-tree-list/SidebarTreeList';
import { SidebarTreeNodeScrollAnchor } from './components/sidebar-tree-node-scroll-anchor/SidebarTreeNodeScrollAnchor';
import styles from './SidebarTree.module.css';
import { SidebarTreeStoreProvider } from './store/sidebarTreeStore';

export type SidebarTreeProps = {
  className?: string;
  hideSearchBarInTree?: boolean;
};

export const SidebarTree = React.memo(function SidebarTreeComponent(props: SidebarTreeProps) {
  const { className } = props;

  const sidebarTreeList = useSearchableTreeList();
  const firstLevelSelectedApiFolderId = useDocsDataModelStore(
    (x) => x.sidebarTree.parentSidebarTreeApiFolderNodes[0]?.id,
  );
  const selectedSidebarTreeNodeKey = useDocsDataModelStore(
    (x) => x.sidebarTree.selectedSidebarTreeNode?.key,
  );

  const initialState = React.useMemo(() => {
    return {
      selectedKey: selectedSidebarTreeNodeKey,
      selectedTime: 0,
    };
  }, [selectedSidebarTreeNodeKey]);

  const navigation = useDocsDataModelStore((x) => x.navigation);

  const { firstLevelNavbarGroup, secondLevelNavbarGroup, thirdLevelNavbarGroup } =
    useLevelNavbarGroup(navigation);

  const hasLevelNavbarGroup = React.useMemo(
    () => firstLevelNavbarGroup || secondLevelNavbarGroup || thirdLevelNavbarGroup,
    [firstLevelNavbarGroup, secondLevelNavbarGroup, thirdLevelNavbarGroup],
  );
  const withScroll = !props.hideSearchBarInTree;

  const treeContent = (
    <div
      className={clsx(
        'px-2 pb-2 pt-5 os:px-5 os:pb-10',
        withScroll
          ? '_tree-scroll-container relative h-full w-full overflow-y-auto'
          : 'w-full flex-1 os:overflow-y-auto',
        className,
      )}
    >
      <RootBackFolders />
      <SidebarTreeList
        list={sidebarTreeList}
        className="w-full"
        indentation={0}
        firstLevelSelectedApiFolderId={firstLevelSelectedApiFolderId}
        isHidden={false}
      />
    </div>
  );
  return (
    <SidebarTreeStoreProvider state={initialState}>
      <SidebarTreeNodeScrollAnchor />
      {props.hideSearchBarInTree ? (
        treeContent
      ) : (
        <>
          <div
            className={clsx('px-2 os:px-5', {
              'mt-2.5': !hasLevelNavbarGroup,
              'mt-5': hasLevelNavbarGroup,
            })}
          >
            <SidebarSearch />
          </div>
          <div className={clsx(styles.treeShadow, 'relative w-full flex-1 overflow-hidden')}>
            {treeContent}
          </div>
        </>
      )}
    </SidebarTreeStoreProvider>
  );
});
